import { Badge, Button, HelpText, Label } from '@/components/ui';

import React, { ReactNode, forwardRef, useId } from 'react';

import { Select as RadixSelect } from '@radix-ui/themes';
import { IconX } from '@tabler/icons-react';
import clsx from 'clsx';

// Base Select component wrappers (like DropdownMenu pattern)
export const SelectRoot = RadixSelect.Root;
export const SelectTrigger = RadixSelect.Trigger;
export const SelectContent = RadixSelect.Content;
export const SelectSeparator = RadixSelect.Separator;
export const SelectLabel = RadixSelect.Label;
export const SelectGroup = RadixSelect.Group;

// SelectItem with asChild support for ListItem
export const SelectItem = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<typeof RadixSelect.Item> & {
    asChild?: boolean;
  }
>((props, ref) => <RadixSelect.Item ref={ref} {...props} />);
SelectItem.displayName = 'SelectItem';

// Mapping for design system sizes (same as TextField)
const sizeMapping = {
  xsmall: '1',
  small: '2',
  medium: '2',
  large: '3',
  default: '3',
} as const;

// Mapping for design system variants (same as TextField)
const variantMapping = {
  filled: 'soft',
  outlined: 'surface',
  ghost: 'surface',
  classic: 'classic',
} as const;

// Mapping for design system colors (same as TextField)
const colorMapping = {
  default: 'gray',
  error: 'ruby',
} as const;

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  secondary?: string;
}

export interface SelectProps {
  label?: string;
  placeholder?: string | ReactNode;
  required?: boolean;
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  options: SelectOption[];
  size?: keyof typeof sizeMapping;
  color?: keyof typeof colorMapping;
  variant?: keyof typeof variantMapping;
  error?: boolean;
  helpText?: string;
  errorMessage?: string;
  className?: string;
  disabled?: boolean;
  renderItem?: (option: SelectOption, isSelected: boolean) => ReactNode;
}

/**
 * High-level Select component with built-in options rendering.
 * For more control, use SelectRoot, SelectTrigger, SelectContent, SelectItem directly.
 */
export const Select = forwardRef<HTMLButtonElement, SelectProps>(
  (
    {
      label,
      placeholder = 'Select option...',
      required,
      value,
      defaultValue,
      onValueChange,
      options = [],
      size = 'large',
      color = 'default',
      variant = 'outlined',
      error = false,
      helpText,
      errorMessage,
      className,
      disabled = false,
      renderItem,
      ...props
    },
    ref
  ) => {
    const selectId = useId();
    const mappedSize = sizeMapping[size] || sizeMapping.default;
    const mappedVariant = variantMapping[variant] || variantMapping.outlined;
    const mappedColor = error
      ? colorMapping.error
      : colorMapping[color] || colorMapping.default;

    // Convert placeholder to string if it's a ReactNode for RadixSelect.Trigger
    const placeholderText =
      typeof placeholder === 'string' ? placeholder : 'Select option...';

    return (
      <div className={clsx('select-wrapper', className)}>
        {label && (
          <Label htmlFor={selectId} required={required}>
            {label}
          </Label>
        )}

        <SelectRoot
          value={value}
          defaultValue={defaultValue}
          onValueChange={onValueChange}
          disabled={disabled}
          size={mappedSize as typeof mappedSize}
        >
          <SelectTrigger
            ref={ref}
            id={selectId}
            placeholder={placeholderText}
            variant={mappedVariant as typeof mappedVariant}
            color={mappedColor as typeof mappedColor}
            radius="large"
            className={clsx(
              `color-${color}`,
              `size-${size}`,
              `variant-${variant}`,
              { error },
              'select-root'
            )}
            data-error={error}
            {...props}
          >
            {value
              ? options.find(opt => opt.value === value)?.label
              : typeof placeholder === 'string'
                ? placeholder
                : null}
          </SelectTrigger>
          <SelectContent variant="soft" className="select-content">
            {(Array.isArray(options) ? options : []).map(option => (
              <SelectItem
                key={option.value}
                value={option.value}
                disabled={option.disabled}
                className={clsx('select-item__wrapper hover-effect', {
                  'select-item--disabled': option.disabled,
                  active: value === option.value,
                })}
              >
                {renderItem ? (
                  renderItem(option, value === option.value)
                ) : (
                  <>
                    {option.startIcon && (
                      <span className="list-item__icon list-item__icon--start">
                        {option.startIcon}
                      </span>
                    )}
                    <div className="list-item__content">
                      <div className="list-item__primary">{option.label}</div>
                      {option.secondary && (
                        <div className="list-item__secondary">
                          {option.secondary}
                        </div>
                      )}
                    </div>
                    {option.endIcon && (
                      <span className="list-item__icon list-item__icon--end">
                        {option.endIcon}
                      </span>
                    )}
                  </>
                )}
              </SelectItem>
            ))}
          </SelectContent>
        </SelectRoot>

        {(errorMessage || helpText) && (
          <HelpText error={error} size={size}>
            {error ? errorMessage : helpText}
          </HelpText>
        )}
      </div>
    );
  }
);

Select.displayName = 'Select';

// Component for multiple selection with Badge
export interface MultiSelectProps
  extends Omit<SelectProps, 'value' | 'onValueChange'> {
  value?: string[];
  onValueChange?: (values: string[]) => void;
  onRemoveValue?: (value: string) => void;
  maxTags?: number;
  badgeVariant?: 'solid' | 'soft' | 'surface' | 'outlined' | 'ghost';
  badgeColor?:
    | 'default'
    | 'primary'
    | 'secondary'
    | 'success'
    | 'warning'
    | 'error'
    | 'info';
}

export const MultiSelect = forwardRef<HTMLButtonElement, MultiSelectProps>(
  (
    {
      value = [],
      onValueChange,
      onRemoveValue,
      options,
      maxTags,
      badgeVariant = 'soft',
      badgeColor = 'primary',
      placeholder = 'Select options...',
      label,
      required,
      ...props
    },
    ref
  ) => {
    const handleValueChange = (newValue: string) => {
      if (!value.includes(newValue)) {
        const newValues = [...value, newValue];
        onValueChange?.(newValues);
      }
    };

    const handleRemoveValue = (valueToRemove: string) => {
      const newValues = value.filter(v => v !== valueToRemove);
      onValueChange?.(newValues);
      onRemoveValue?.(valueToRemove);
    };

    const selectedOptions = value
      .map(v => options.find(opt => opt.value === v))
      .filter(Boolean) as SelectOption[];
    const availableOptions = options.filter(opt => !value.includes(opt.value));

    return (
      <div className={clsx('multi-select-wrapper', props.className)}>
        {label && <Label required={required}>{label}</Label>}

        {/* Select for adding new values */}
        <Select
          ref={ref}
          {...props}
          label={undefined} // Remove label as it is already displayed above
          required={required} // Remove required as it is already displayed in label above
          helpText={undefined} // Remove helpText as it is displayed in MultiSelect
          errorMessage={undefined} // Remove errorMessage as it is displayed in MultiSelect
          placeholder={selectedOptions.length > 0 ? 'Add more...' : placeholder}
          options={availableOptions}
          onValueChange={handleValueChange}
          value=""
        />

        {/* Display selected values as Badge */}
        {selectedOptions.length > 0 && (
          <div className="multi-select__tags">
            {selectedOptions.slice(0, maxTags).map(option => (
              <Badge
                key={option.value}
                variant={badgeVariant}
                color={badgeColor}
                size="small"
                startIcon={option.startIcon}
                endIcon={
                  <Button
                    type="button"
                    isIconOnly
                    size="xsmall"
                    variant="ghost"
                    onClick={() => handleRemoveValue(option.value)}
                    className="multi-select__tag-remove"
                    aria-label={`Remove ${option.label}`}
                  >
                    <IconX />
                  </Button>
                }
                className="multi-select__tag"
              >
                {option.label}
              </Badge>
            ))}
            {maxTags && selectedOptions.length > maxTags && (
              <Badge
                variant="surface"
                color="gray"
                size="small"
                className="multi-select__tag-overflow"
              >
                +{selectedOptions.length - maxTags} more
              </Badge>
            )}
          </div>
        )}

        {(props.errorMessage || props.helpText) && (
          <HelpText error={props.error} size={props.size}>
            {props.error ? props.errorMessage : props.helpText}
          </HelpText>
        )}
      </div>
    );
  }
);

MultiSelect.displayName = 'MultiSelect';

// Usage examples:
//
// High-level Select (as before):
// <Select options={options} value={value} onValueChange={setValue} />
//
// Low-level composable Select with custom ListItem:
// <SelectRoot value={value} onValueChange={setValue}>
//   <SelectTrigger>Select option...</SelectTrigger>
//   <SelectContent>
//     <SelectItem value="option1" asChild>
//       <ListItem
//         primary="Custom Option 1"
//         secondary="With description"
//         startIcon={<Icon />}
//       />
//     </SelectItem>
//     <SelectSeparator />
//     <SelectItem value="option2" asChild>
//       <ListItem primary="Custom Option 2" />
//     </SelectItem>
//   </SelectContent>
// </SelectRoot>
